import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Gift, Loader2, Check, Sparkles, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function RedeemGift() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const { user } = useAuth();

  const { data: gift, isLoading, isError } = useQuery({
    queryKey: ["gift", token],
    queryFn: () => api.get(`/gift/${token}`).then((r) => r.data),
    enabled: !!token,
    retry: false,
  });

  const email = gift?.recipient_email || "";
  const isRecipient = user && email && user.email?.toLowerCase() === email.toLowerCase();

  return (
    <div className="min-h-[80vh] grid place-items-center px-6 py-16" data-testid="redeem-gift-page">
      <div className="w-full max-w-lg eyw-card p-10 text-center">
        <span className="grid place-items-center h-14 w-14 rounded-full bg-[#D98E73] text-white mb-6 mx-auto"><Gift size={24} /></span>

        {!token || isError ? (
          <>
            <h1 className="font-serif text-3xl text-[#1A242B] mb-3">This gift link isn't valid</h1>
            <p className="text-[#4A5D6B] mb-6">The link may have expired or already been used. If you believe this is a mistake, please call us at 859-979-5505 and we'll help.</p>
            <Link to="/" className="eyw-btn inline-flex">Back home</Link>
          </>
        ) : isLoading ? (
          <div className="py-10"><Loader2 className="animate-spin text-[#144F75] mx-auto" /></div>
        ) : (
          <>
            <p className="eyw-label mb-2">You've received a gift</p>
            <h1 className="font-serif text-3xl lg:text-4xl text-[#1A242B] mb-3">
              {gift.giver_name ? `${gift.giver_name} gifted you` : "A gift is waiting for you"}
            </h1>

            <div className="rounded-2xl border border-[#D98E73]/40 bg-[#D98E73]/[0.06] p-6 my-6" data-testid="gift-summary">
              <p className="font-serif text-2xl text-[#144F75]" data-testid="gift-package-name">{gift.package_name}</p>
              <p className="text-[#4A5D6B] mt-1"><b>{gift.sessions}</b> wellness session{gift.sessions === 1 ? "" : "s"} to enjoy</p>
              {gift.gift_message && (
                <p className="italic text-[#4A5D6B] mt-4 border-l-2 border-[#D98E73] pl-3 text-left">“{gift.gift_message}”</p>
              )}
            </div>

            {isRecipient ? (
              <>
                <p className="text-[#4A5D6B] mb-5 flex items-center justify-center gap-2"><Check size={16} className="text-[#5C8A72]" /> Your sessions are in your account.</p>
                <button onClick={() => nav("/dashboard")} data-testid="redeem-view-dashboard" className="eyw-btn w-full justify-center">
                  View my sessions & book <ArrowRight size={16} />
                </button>
              </>
            ) : (
              <>
                <p className="text-[#4A5D6B] mb-2">Your sessions are reserved for <b>{email}</b>.</p>
                <p className="text-sm text-[#8FA89B] mb-6">Sign in (or create a free account) with that email to claim them and book whenever you're ready.</p>
                <div className="space-y-3">
                  <button onClick={() => nav(`/login?email=${encodeURIComponent(email)}&next=/dashboard`)} data-testid="redeem-signin" className="eyw-btn w-full justify-center">
                    <Sparkles size={16} /> Sign in to claim
                  </button>
                  <button onClick={() => nav(`/join?email=${encodeURIComponent(email)}`)} data-testid="redeem-create-account" className="eyw-btn-ghost w-full justify-center">
                    Create my free account
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
